// All structured marketing content for the BillPortal website.
// Authored originally for BillPortal — no third-party copy, no client names.
// Icons are referenced by name and resolved by components/Icon.js (lucide-react).

import { IMAGES } from './images';

/* ------------------------------------------------------------------ */
/* "Try Data Extraction" journey — built on the product-app side.      */
/* The website's only job is to link here; the app handles the entire  */
/* email → OTP → workspace → parse → register flow. `src` is used for   */
/* CRM attribution. Change the base in ONE place if the domain differs. */
/* NOTE: backend env uses app.billportal.ai — confirm .io vs .ai.       */
/* ------------------------------------------------------------------ */
export const TRY_EXTRACTION_BASE = 'https://app.billportal.ai';
export const tryExtractionUrl = (src) => `${TRY_EXTRACTION_BASE}/api/try/enter?src=${encodeURIComponent(src)}`;

/* ------------------------------------------------------------------ */
/* Hero / trust stats                                                  */
/* ------------------------------------------------------------------ */
export const HERO_STATS = [
  { value: '$30M+', label: 'Payments processed', icon: 'Banknote' },
  { value: '22,000+', label: 'Bills processed per portfolio', icon: 'FileText' },
  { value: '24–48h', label: 'Collection, parsing & resolution SLAs', icon: 'Clock' },
  { value: '99%', label: 'Platform uptime target', icon: 'ShieldCheck' },
];

// Rotating shimmer words for the hero title — the utilities/bills you control.
export const HERO_ROTATORS = ['Electricity', 'Water & Sewer', 'Natural Gas', 'Waste', 'Heating Oil', 'Telco', 'Fuel'];

// Positioning chips for the hero: self-serve, AI-orchestrated, human-in-the-loop, managed.
export const HERO_MODES = [
  { icon: 'Cpu', label: 'AI-orchestrated' },
  { icon: 'Users', label: 'Human-in-the-loop' },
  { icon: 'Workflow', label: 'Self-serve' },
  { icon: 'LifeBuoy', label: 'Fully In Control' },
];

/* ------------------------------------------------------------------ */
/* Platform pillars — the end-to-end lifecycle                         */
/* ------------------------------------------------------------------ */
export const PILLARS = [
  {
    key: 'acquire',
    step: '01',
    icon: 'Bot',
    title: 'Acquire',
    summary: 'Collect every bill, from every source',
    description:
      'Collection Agents log into supplier portals across most U.S. utilities on a schedule, while inboxes, manual uploads, SFTP feeds and scanned postal mail all land in one secure, centralised repository.',
    points: ['Collection Agents for U.S. portals', 'Email & manual upload', 'SFTP & scanned mail', 'Interval & meter data'],
  },
  {
    key: 'parse',
    step: '02',
    icon: 'Sparkles',
    title: 'Parse',
    summary: 'Accurate data capture across every commodity',
    description:
      'AI Agents read every bill with high accuracy and capture all the data that matters — across electricity, water, waste, gas, heating oil, telco and fuels — including charges, usage and meter reads, validated against the figures on the page.',
    points: ['All commodities & charge types', 'Usage & meter reads captured', 'Arithmetic & tolerance checks', 'Page-level traceability'],
  },
  {
    key: 'validate',
    step: '03',
    icon: 'BadgeCheck',
    title: 'Validate',
    summary: 'Catch issues before they cost you',
    description:
      'Integrity, tolerance and comparison checks flag anomalies, while tracking of missing, late-arriving and overdue bills keeps your data complete and your payments on time.',
    points: ['Integrity & comparison checks', 'Missing & late-bill tracking', 'Supplier resolution workspace', 'Exception management'],
  },
  {
    key: 'pay',
    step: '04',
    icon: 'Landmark',
    title: 'Pay',
    summary: 'Pay vendors in bulk, on time',
    description:
      'Consolidate approved bills into payment files with GL and cost-code allocation, then pay your vendors in bulk by ACH — with credit card and cheque available through our managed service and banking partner.',
    points: ['Bulk payment files & approvals', 'ACH (NACHA CTX, CCD+)', 'Card & cheque (managed)', 'Remittance & accruals'],
  },
  {
    key: 'report',
    step: '05',
    icon: 'BarChart3',
    title: 'Report',
    summary: 'One dataset, many outcomes',
    description:
      'The same validated data powers financial control, environmental reporting and ENERGY STAR® compliance, and feeds budgeting when you plan to procure or go to market.',
    points: ['Spend & variance analytics', 'Emissions (Scope 1/2/3)', 'ENERGY STAR® compliance', 'Budgeting & procurement'],
  },
];

/* ------------------------------------------------------------------ */
/* Commodities captured                                                */
/* ------------------------------------------------------------------ */
export const COMMODITIES = [
  { name: 'Electricity', icon: 'Zap' },
  { name: 'Water & Sewer', icon: 'Droplets' },
  { name: 'Gas', icon: 'Flame' },
  { name: 'Waste', icon: 'Trash2' },
  { name: 'Heating Oil', icon: 'Gauge' },
  { name: 'Telco', icon: 'Network' },
  { name: 'Fuels', icon: 'BatteryCharging' },
];

export const PARSE_USES = [
  { icon: 'Landmark', title: 'Accurate payments', text: 'Every charge, tax and adjustment captured so payments are right the first time.' },
  { icon: 'Leaf', title: 'Environmental reporting', text: 'Consumption by commodity feeds emissions and ENERGY STAR® reporting.' },
  { icon: 'Target', title: 'Budgeting & procurement', text: 'A clean dataset to budget, forecast and plan when you go to market.' },
  { icon: 'Gauge', title: 'Meter reads', text: 'Meter reads captured from bills power compliance, rebilling and ratio billing.' },
];

/* ------------------------------------------------------------------ */
/* Bill tracking & exceptions                                          */
/* ------------------------------------------------------------------ */
export const BILL_TRACKING = [
  { icon: 'Search', title: 'Missing bills', text: 'Know exactly which bills should have arrived but haven’t — by property, account and supplier — so gaps never turn into back-bills.' },
  { icon: 'Clock', title: 'Late-arriving bills', text: 'Flag bills that arrive after their expected window and reconcile them automatically, especially during a transition from a previous provider.' },
  { icon: 'AlertTriangle', title: 'Overdue invoices', text: 'Surface invoices approaching or past their due date and trigger action before they become late fees or disconnections.' },
];

/* ------------------------------------------------------------------ */
/* Acquisition channels                                                */
/* ------------------------------------------------------------------ */
export const CHANNELS = [
  { icon: 'Bot', title: 'Collection Agents (portals)', text: 'Automated agents acquire bills and interval data from supplier portals across most U.S. utilities.' },
  { icon: 'Mail', title: 'Email capture', text: 'Forward supplier emails or use a dedicated inbox; attachments are ingested automatically.' },
  { icon: 'Upload', title: 'Manual upload', text: 'Drag-and-drop PDFs and documents straight into the workspace when you need to.' },
  { icon: 'Server', title: 'SFTP feeds', text: 'Bulk supplier and EDI feeds are pulled securely over SFTP and normalised on arrival.' },
  { icon: 'ScanLine', title: 'Postal mail', text: 'Bills that arrive by post go to a mailing address we set up, are scanned, and routed into the same pipeline.' },
  { icon: 'Database', title: 'Interval & meter data', text: 'We pull and store interval and meter data to validate every charge against actual usage.' },
];

/* ------------------------------------------------------------------ */
/* Payment methods                                                     */
/* ------------------------------------------------------------------ */
export const PAYMENT_METHODS = [
  { icon: 'Landmark', title: 'ACH (bulk)', text: 'NACHA-compliant ACH including CTX and CCD+ addenda — pay your vendors in bulk through our banking partner.' },
  { icon: 'CreditCard', title: 'Credit card', text: 'Pay suppliers by card where supported, available through our managed service.' },
  { icon: 'FileCheck', title: 'Cheque', text: 'Cheque payments handled through our managed service and banking partner.' },
];

export const PAYMENT_FEATURES = [
  { icon: 'FileSpreadsheet', title: 'Payment files', text: 'Run weekly or fortnightly payment files with GL and cost-code allocation, and pay in bulk on your schedule.' },
  { icon: 'Receipt', title: 'Remittance & reports', text: 'Remittance attached to every payment, plus full payment reports including bank transactions.' },
  { icon: 'RefreshCw', title: 'Refunds & exceptions', text: 'Track refunds, emergency payments and variances with a complete transaction ledger.' },
  { icon: 'Wallet', title: 'Funding & fund pulls', text: 'Generate funding requests, pull funds and reconcile against bank statements automatically.' },
];

/* ------------------------------------------------------------------ */
/* Reporting & sustainability                                          */
/* ------------------------------------------------------------------ */
export const REPORTING = [
  { icon: 'Leaf', title: 'Emissions accounting', text: 'Calculate Scope 1, 2 and 3 emissions from bills and interval data across your portfolio.' },
  { icon: 'BadgeCheck', title: 'ENERGY STAR® compliance', text: 'Feed validated consumption straight into ENERGY STAR® Portfolio Manager for benchmarking and compliance.' },
  { icon: 'Target', title: 'Targets & budgets', text: 'Lock budgets, set reduction targets and monitor actuals with alerts before you overrun.' },
  { icon: 'Sun', title: 'Solar, batteries & DER', text: 'Track generation, storage and grid usage to measure renewable performance.' },
];

/* ------------------------------------------------------------------ */
/* Managed services                                                    */
/* ------------------------------------------------------------------ */
export const SERVICES = [
  { icon: 'LifeBuoy', title: 'Dedicated support', text: 'A dedicated, friendly support team resolves billing queries quickly, with tailored SLAs.' },
  { icon: 'ClipboardCheck', title: 'Data accuracy & auditing', text: 'Precise validation and complete audit trails keep billing accurate and compliant.' },
  { icon: 'Rocket', title: 'Onboarding & project management', text: 'We manage the transition end-to-end — from credentials to go-live and handover.' },
  { icon: 'Workflow', title: 'Managed BAU processes', text: 'We run day-to-day bill processing and payments so your team can focus on exceptions.' },
  { icon: 'Plug', title: 'Customisation & integration', text: 'Flexible integrations and tailored configuration fit BillPortal to how you operate.' },
  { icon: 'Users', title: 'Dedicated team', text: 'Account managers and engineers partnered to your portfolio for accuracy and speed.' },
];

/* ------------------------------------------------------------------ */
/* Onboarding overview (seamless experience)                           */
/* ------------------------------------------------------------------ */
export const ONBOARDING_STEPS = [
  { icon: 'Search', title: 'Discover & reconcile', text: 'We gather your sites, accounts and suppliers and reconcile the onboarding data with you.' },
  { icon: 'Mail', title: 'Access & mail setup', text: 'We set up vendor portal credentials and a mailing address to capture bills that arrive by post.' },
  { icon: 'Workflow', title: 'Configure & validate', text: 'We configure Collection Agents, validation and your property, common-area and unit structure, then run UAT.' },
  { icon: 'DatabaseZap', title: 'Migrate history', text: 'We migrate and reconcile historical data so nothing is lost in the transition from your incumbent.' },
  { icon: 'Rocket', title: 'Go-live & hyper care', text: 'We cut over with a dedicated team monitoring SLAs for a seamless start.' },
];

/* ------------------------------------------------------------------ */
/* Integrations                                                        */
/* ------------------------------------------------------------------ */
export const INTEGRATIONS = [
  { name: 'ERP & AP systems', detail: 'Yardi, SAP, Xero, JD Edwards & more', icon: 'Network' },
  { name: 'Two-way ERP sync', detail: 'Straight-through AP processing', icon: 'RefreshCw' },
  { name: 'Sustainability platforms', detail: 'Envizi, Watershed & others', icon: 'Leaf' },
  { name: 'ENERGY STAR® Portfolio Manager', detail: 'Benchmarking & compliance', icon: 'BadgeCheck' },
  { name: 'Energy systems (EMS / DER)', detail: 'Solar, batteries, EV', icon: 'Sun' },
  { name: 'Banking & payments', detail: 'ACH, card & cheque rails', icon: 'Landmark' },
];

/* ------------------------------------------------------------------ */
/* Pricing — credit-based, pay-for-what-you-use (per account)          */
/* Purchases, top-ups and auto-debit are handled inside the app.       */
/* ------------------------------------------------------------------ */

// Core pricing model — simple per-bill pricing. Costs include collection,
// parsing (bill data extraction) and full UBM access.
export const PRICING_MODEL = {
  // Free plan
  freeBills: 5,             // bills / month included (resets monthly)
  freeRate: 1.39,           // $ per bill beyond the monthly 5
  freeAiCreditsOneTime: 5,  // $ one-time AI credits (50 credits)
  // Pro plan
  proPrice: 30,             // $ / month (auto-charged, cancel anytime)
  proBills: 30,             // bills / month parsing included
  proRate: 1.0,             // $ per bill beyond the monthly 30 (prepaid)
  proAiCreditsMonthly: 5,   // $ / month AI credits (50 credits) included
  proAiCreditsWelcome: 5,   // $ one-time welcome AI credits (50 credits)
  // Add-ons
  postalRate: 1.5,          // $ per bill for postal mail capture (unchanged)
};

export const PRICING_TIERS = [
  {
    name: 'Free',
    price: '$0',
    priceSub: '/ month · no card',
    range: '5 bills / month included',
    highlight: false,
    cta: 'Start free',
    href: 'https://app.billportal.ai/signup?src=web&plan=free',
    blurb: 'Start free — 5 bills every month, no card required. Pay only if you go beyond, or upgrade to Pro.',
    features: [
      '5 bills / month included (resets monthly)',
      '$5 in AI credits (one-time · 50 credits) for Astrid, portal scraping & data migration',
      '$1.39 per bill beyond the monthly 5 — or upgrade to Pro',
      'Top up AI credits or bill credits anytime from Settings',
      'AI bill acquisition (portals, email, upload) & parsing across all commodities',
      'Validation, exception management & standard dashboards',
    ],
  },
  {
    name: 'Pro',
    price: '$30',
    priceSub: 'per month · cancel anytime',
    range: '30 bills / month included',
    highlight: true,
    cta: 'Get started',
    href: 'https://app.billportal.ai/signup?src=web&plan=pro',
    blurb: 'Auto-charged monthly, cancel anytime — more included bills, a lower overage rate and monthly AI credits.',
    features: [
      'Everything in Free',
      '30 bills / month parsing included (resets monthly)',
      '$1.00 per bill beyond the monthly 30 (prepaid)',
      '$5 / month in AI credits (50 credits) included + $5 (50) one-time welcome',
      'Set a minimum balance & auto-recharge for bill credits',
      'Missing, late & overdue bill tracking + ACH payment files',
    ],
    note: 'Postal mail capture available on paid plans at +$1.50 / bill.',
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    priceSub: 'Contact us',
    range: 'Managed & white-labelled',
    highlight: false,
    cta: 'Contact us',
    ctaTo: '/contact',
    blurb: 'A fully managed, customised BillPortal — set up on dedicated infrastructure and tailored to your organisation.',
    features: [
      'Everything in Pro',
      'Platform customisation & white-labelling',
      'Dedicated infrastructure & data residency',
      'Custom integrations (ERP / AP / bank)',
      'Fully managed service option',
      'Dedicated account & support team',
      'Bespoke SLAs (24h response)',
    ],
    note: 'Postal mail capture available at +$1.50 / bill.',
  },
];

export const PRICING_FAQ = [
  { q: 'How does pricing work?', a: 'BillPortal is simple, pay-for-what-you-use pricing. Free is $0/month and includes 5 bills every month (no card). Pro is $30/month and includes 30 bills every month, then $1.00 per bill beyond that. On Free, bills beyond the monthly 5 are $1.39 each — or you can upgrade to Pro. Every price includes collection, parsing (bill data extraction) and full UBM access. You purchase, top up and set auto-recharge for credits inside the app.' },
  { q: 'What counts as a “bill”?', a: 'One bill is one processed utility bill — collected from a portal, email or upload, then parsed and validated across its commodities, with full UBM access to the data. Your monthly included bills reset each month.' },
  { q: 'What does “5 bills free” mean?', a: 'The Free plan includes 5 bills every month at no cost, and they reset monthly. Go beyond 5 and each extra bill is $1.39 — or upgrade to Pro for 30 included bills and a lower $1.00 overage rate. No card is required to start on Free.' },
  { q: 'Is there a free option?', a: 'Yes — Free is $0/month forever with 5 bills every month and $5 in one-time AI credits (50 credits) for Astrid, portal scraping and data migration. No card required.' },
  { q: 'How do I estimate my monthly cost?', a: 'Enter how many bills you expect to process each month in the calculator above. We compare Free (5 included, $1.39/bill after) against Pro ($30/month, 30 included, $1.00/bill after) and show you the cheaper option, plus any optional postal capture.' },
  { q: 'What are AI credits?', a: 'AI credits are a separate prepaid wallet that powers Astrid and the AI agents — onboarding accounts, scraping supplier portals, migrating data from a previous provider, generating reports and contacting suppliers in bulk. Free includes $5 (50 credits) one-time; Pro includes $5 (50 credits) every month plus a $5 (50) one-time welcome. Top up anytime from Settings.' },
  { q: 'What about bills that arrive by post?', a: 'Postal mail capture is an optional add-on at $1.50 per bill, available on paid plans; it is not included on Free. During onboarding we set up a mailing address so postal bills are scanned and routed into the same pipeline.' },
  { q: 'How do credits and payments work?', a: 'Credits are prepaid and managed inside the BillPortal app — you register, purchase credits, top up and set a minimum balance with auto-recharge there. This website shows pricing and an estimate only.' },
  { q: 'Do you offer a fully managed service?', a: 'Yes. Our Enterprise plan is a fully managed, customised BillPortal — set up on dedicated infrastructure with platform customisation and white-labelling, custom integrations (ERP / AP / bank), a dedicated account and support team, and bespoke SLAs (24h response). Contact us to scope it.' },
  { q: 'Do you integrate with our ERP or AP system?', a: 'Pro generates standard AP and funding files plus ACH payment files that import into most systems. Deeper two-way ERP / AP integration (for example Yardi, SAP, Xero and JD Edwards) is available on Enterprise, scoped together during onboarding.' },
  { q: 'How do Portal Agents know when to collect a bill?', a: 'When you set up a Portal Agent for an account (with its portal credentials), it collects the two most recent bills and records a pattern from the bill issue date, billing period and due date. Based on that pattern the Portal Agent auto-schedules itself to collect each new bill as it becomes available — no manual reminders needed.' },
];

/* ------------------------------------------------------------------ */
/* Solutions (by segment)                                              */
/* ------------------------------------------------------------------ */
export const SOLUTIONS = {
  multifamily: {
    slug: 'multifamily',
    name: 'Multifamily',
    icon: 'Building2',
    image: IMAGES.multifamily,
    tagline: 'Utility Expense Management & Vacant Cost Recovery for residential portfolios.',
    headline: 'Utility management isn’t just about paying bills',
    intro:
      'It’s about controlling cost, consumption and risk across every property. BillPortal turns every multifamily utility bill into one source of truth — captured, validated, approved, allocated and analyzed, so nothing slips through the cracks.',
    intelligence: {
      eyebrow: 'Utility Bill Intelligence',
      audience: 'For multifamily & commercial real estate',
      hubIcon: 'Users',
      hubTitle: 'One workspace',
      hubSub: 'for every multifamily team',
      personas: ['Property Managers', 'Finance', 'Accounts Payable', 'Operations'],
      flow: [
        { label: 'Captured', sub: 'No missed bills', icon: 'ScanLine' },
        { label: 'Validated', sub: 'Fewer errors', icon: 'ShieldCheck' },
        { label: 'Approved', sub: 'Faster cycles', icon: 'FileCheck' },
        { label: 'Allocated', sub: 'Accurate recovery', icon: 'Building2' },
        { label: 'Analyzed', sub: 'Smarter decisions', icon: 'BarChart3' },
      ],
      result: 'Lower cost. Stronger control. Better NOI.',
      chips: [
        { label: 'Pay on time', icon: 'CheckCircle2' },
        { label: 'Recover more', icon: 'Banknote' },
        { label: 'Reduce waste', icon: 'Leaf' },
        { label: 'Protect NOI', icon: 'ShieldCheck' },
      ],
    },
    problems: [
      'Vacant-unit utility costs quietly eroding NOI',
      'Bills arriving by post for vacant units with tight pay-by dates',
      'Common-area and unit costs tangled together',
      'Missing, late and overdue bills during incumbent transitions',
    ],
    capabilities: [
      { icon: 'Home', title: 'Vacant Cost Recovery (VCR)', text: 'Identify and recover utility costs on vacant units with occupancy-aware data.' },
      { icon: 'Mail', title: 'Vacant-unit mail flow', text: 'We set up a mailing address at onboarding so vacant-unit bills are processed and paid on time.' },
      { icon: 'Layers', title: 'Property, common area & units', text: 'Model each property with common areas and units separated for fully organised bills.' },
      { icon: 'Bot', title: 'Collection Agents', text: 'Vendor portal credentials and automated agents collect bills in time to pay them.' },
      { icon: 'Landmark', title: 'Bulk ACH payments', text: 'Run weekly or fortnightly payment files and pay common-area and unit bills in bulk.' },
      { icon: 'Search', title: 'Missing & late tracking', text: 'Chase missing, late-arriving and overdue bills — especially during transition.' },
    ],
    narrative: [
      { title: 'Why operators take control of utilities', text: 'For multifamily operators, utilities are a major, recoverable cost. The biggest driver is vacant-unit cost: when a unit is empty, its utility charges fall back on the operator. Recovering those costs — and keeping common-area spend in check — protects NOI across the portfolio.' },
      { title: 'Acquire → parse → validate → pay', text: 'BillPortal runs the full lifecycle. We acquire bills from vendor portals, email, upload, SFTP and post; parse them accurately across every commodity; validate the data; and pay your vendors in bulk by ACH on your schedule.' },
      { title: 'Handling vacant units', text: 'When units are vacant, bills often arrive by post to a mailing address we set up during onboarding. That bill is processed and paid first, then we establish vendor portal credentials to collect future bills in time to make timely payments — avoiding late fees and disconnections.' },
      { title: 'Common areas, units and transitions', text: 'Storing each property with common areas and units separated keeps every bill organised — the starting point for chasing missing, late-arriving and overdue bills, which matters most during the transition from a previous incumbent.' },
    ],
    outcomes: [
      'Recovered vacant-unit utility costs and protected NOI',
      'Timely, bulk payments with no missed due dates',
      'Fully organised bills by property, common area and unit',
      'A clean transition from your previous incumbent',
    ],
    faq: [
      { q: 'How do you handle payments when we transition from a previous provider?', a: 'The platform is built to support the standard multifamily payments process through transition and cutover, so you stay in control from day one. In the first month, you pay the new charges. In the second month, where there is a balance brought forward (BBF), you settle the total amount due on those bills. From the third month onward, you move to your standard payment cycle. The platform applies this staged logic automatically. If you choose our managed service, we can set the whole process up and run the transition for you. Talk to our friendly support team today to learn more.' },
      { q: 'How do you manage utilities on vacant units?', a: 'We help you set up a mailing address during onboarding so vacant-unit bills are captured, processed and paid first, then support you in establishing vendor portal credentials so future bills are collected in time to pay them. You keep full visibility of every vacant-unit and common-area bill in one place.' },
      { q: 'Can we run our own payment files?', a: 'Yes — you run weekly or fortnightly payment files and pay your vendors in bulk via ACH directly on the platform, fully in your control. Prefer credit card or cheque? Our managed service can operate those for you — just talk to our friendly support team.' },
      { q: 'How do you organise bills across a property?', a: 'You model each property with common areas and units separated — with our support during setup — so every bill is correctly organised and owned by you. That structure is the foundation for chasing missing, late-arriving and overdue bills, especially during an incumbent transition.' },
      { q: 'What payment reports do we get?', a: 'You get full access to all payment-related reports — bank transactions, payment files, remittance and variance reports — so your finance team always has an audit-ready view.' },
    ],
    caseStudy: 'multifamily-portfolio-uem',
  },
  'commercial-industrial': {
    slug: 'commercial-industrial',
    name: 'Commercial & Industrial',
    icon: 'Factory',
    image: IMAGES.commercial,
    tagline: 'Centralised bill data, payment files and environmental reporting at global scale.',
    headline: 'One platform for global commercial & industrial portfolios',
    intro:
      'Collect bills and bill data through payment files and feeds, eliminate double-handling between AP, finance, procurement and sustainability, and report with confidence — across countries and currencies.',
    intelligence: {
      eyebrow: 'Utility Bill Intelligence',
      audience: 'For global commercial & industrial portfolios',
      accent: '#3B82F6',
      accent2: '#22D3EE',
      hubIcon: 'Globe',
      hubTitle: 'One platform',
      hubSub: 'every country & currency',
      personas: ['Group Finance', 'AP / Shared Services', 'Procurement', 'Sustainability'],
      flow: [
        { label: 'Centralize', sub: 'Every entity', icon: 'Globe' },
        { label: 'Integrate', sub: 'Two-way ERP', icon: 'Network' },
        { label: 'Validate', sub: 'Fewer errors', icon: 'ShieldCheck' },
        { label: 'Pay', sub: 'Straight-through AP', icon: 'Landmark' },
        { label: 'Report', sub: 'Audit-ready', icon: 'BarChart3' },
      ],
      result: 'Lower cost. Straight-through AP. Audit-ready.',
      chips: [
        { label: 'Multi-currency', icon: 'Globe' },
        { label: 'ERP two-way', icon: 'Network' },
        { label: 'Scope 1/2/3', icon: 'Leaf' },
        { label: 'SLAs & controls', icon: 'ShieldCheck' },
      ],
    },
    problems: [
      'Resource-heavy, error-prone manual bill management',
      'Data silos and double-handling across teams',
      'Reliance on external vendors and fragmented tooling',
      'Late payments and limited portfolio-wide visibility',
    ],
    capabilities: [
      { icon: 'Globe', title: 'Global portfolio management', text: 'Centralise data across countries and entities in one platform.' },
      { icon: 'FileSpreadsheet', title: 'Bill data via payment files', text: 'Acquire, validate and structure bill data through payment files and feeds.' },
      { icon: 'Network', title: 'Two-way ERP integration', text: 'Straight-through AP processing with Yardi, SAP, JD Edwards and more.' },
      { icon: 'Leaf', title: 'Environmental reporting', text: 'Emissions accounting and sustainability integrations from validated data.' },
      { icon: 'ShieldCheck', title: 'Controls & audit trails', text: 'End-to-end audit history and SLAs for compliance and accountability.' },
      { icon: 'TrendingUp', title: 'Financial control', text: 'Budgets, targets, accruals and variance analysis for predictable spend.' },
    ],
    outcomes: [
      'Significant reduction in payment-processing cost',
      'Elimination of late-payment penalties',
      'Centralised, data-driven portfolio visibility',
      '100% automation of key operational reports',
    ],
    faq: [
      { q: 'Can you operate across multiple countries and currencies?', a: 'Yes — the platform centralises bill data across countries and entities so your teams work from one source of truth, with data stored in your country to support local data-residency requirements. You own that data and the view of your whole portfolio.' },
      { q: 'Do you integrate with our ERP and AP systems?', a: 'Yes — two-way integration with systems such as Yardi, SAP and JD Edwards gives your Accounts Payable team straight-through processing, including approvals and remittances, so control stays inside your own finance stack.' },
      { q: 'How do you support environmental reporting?', a: 'Validated consumption data feeds your emissions accounting (Scope 1/2/3) and ENERGY STAR® Portfolio Manager, so your sustainability team can benchmark and report with confidence from data you own.' },
      { q: 'How are bills collected at scale?', a: 'The platform collects bills through Collection Agents on supplier portals, email, SFTP feeds, manual upload, post and payment files — all into one repository you control, with our support to onboard new vendors and portals.' },
    ],
    caseStudy: 'global-commercial-real-estate',
  },
  'energy-brokers': {
    slug: 'energy-brokers',
    name: 'Energy Brokers',
    icon: 'Briefcase',
    image: IMAGES.grid,
    tagline: 'A data foundation for contracting, budgeting and proactive energy management.',
    headline: 'Help your clients manage energy spend — proactively',
    intro:
      'BillPortal gives energy brokers the data foundation — collection and parsing of bills — that powers contracting and budgeting modules, so you can help clients manage spend and win on service.',
    intelligence: {
      eyebrow: 'Utility Bill Intelligence',
      audience: 'For energy brokers & channel partners',
      accent: '#F59E0B',
      accent2: '#FBBF24',
      hubIcon: 'Briefcase',
      hubTitle: 'Your branded portal',
      hubSub: 'for every client',
      personas: ['Brokers', 'Clients', 'Analysts', 'Account Managers'],
      flow: [
        { label: 'Collect', sub: 'Across clients', icon: 'Database' },
        { label: 'Parse', sub: 'Accurate data', icon: 'ScanLine' },
        { label: 'Contract', sub: 'Rates & terms', icon: 'FileSignature' },
        { label: 'Budget', sub: 'Targets & alerts', icon: 'Target' },
        { label: 'Advise', sub: 'Win on service', icon: 'TrendingUp' },
      ],
      result: 'More value beyond procurement.',
      chips: [
        { label: 'White-label', icon: 'Sparkles' },
        { label: 'Contract data', icon: 'FileSignature' },
        { label: 'Budget alerts', icon: 'Target' },
        { label: 'Client retention', icon: 'Users' },
      ],
    },
    problems: [
      'No reliable, structured data foundation across clients',
      'Manual effort to gather bills for contracting and budgeting',
      'Hard to demonstrate ongoing value beyond procurement',
      'Fragmented tools for data, payments and reporting',
    ],
    capabilities: [
      { icon: 'Database', title: 'Data foundation', text: 'Automated collection and AI parsing of bills across your client book.' },
      { icon: 'FileSignature', title: 'Contracting module', text: 'Manage contracts and rates against validated consumption data.' },
      { icon: 'Target', title: 'Budgeting module', text: 'Lock budgets, set targets and reforecast with alerts on anomalies.' },
      { icon: 'Sparkles', title: 'White-label & channel partner', text: 'Offer a branded portal to your clients as a channel partner.' },
      { icon: 'BarChart3', title: 'Client analytics', text: 'Benchmark sites, track spend and surface savings opportunities.' },
      { icon: 'Leaf', title: 'Sustainability add-on', text: 'Extend into emissions and ENERGY STAR® reporting for client value.' },
    ],
    outcomes: [
      'A repeatable data foundation across every client',
      'New recurring value beyond procurement',
      'Faster, data-backed contracting and budgeting',
      'A branded experience that strengthens client retention',
    ],
    faq: [
      { q: 'Can we white-label the platform for our clients?', a: 'Yes — you offer a branded portal to your clients as a channel partner, with your own look and feel, so the relationship and the data stay yours.' },
      { q: 'What data do the contracting and budgeting modules use?', a: 'They build on your data foundation — automated collection and accurate parsing of bills — so contracting and budgeting are grounded in validated consumption and cost that you own across your client book.' },
      { q: 'Can you handle payments for our clients too?', a: 'Yes, optionally. Your clients can run bulk ACH payment files on the platform themselves, and our managed service can operate card and cheque payments where needed — you stay in control of the model.' },
    ],
    caseStudy: 'global-commercial-real-estate',
  },
  'bill-validation': {
    slug: 'bill-validation',
    name: 'Bill Validation',
    icon: 'BadgeCheck',
    image: IMAGES.heroAnalytics,
    tagline: 'The data backbone for utility bill validation and audit providers.',
    headline: 'Power your bill validation practice with clean, structured data',
    intro:
      'BillPortal supports utility bill validation and audit providers with the data backbone — automated acquisition, accurate AI parsing and structured, validated data across commodities — so your specialists can focus on identifying issues and savings while we handle the heavy lifting.',
    intelligence: {
      eyebrow: 'Utility Bill Intelligence',
      audience: 'For utility bill validation & audit providers',
      accent: '#10B981',
      accent2: '#34D399',
      hubIcon: 'BadgeCheck',
      hubTitle: 'Clean, validated data',
      hubSub: 'at scale',
      personas: ['Auditors', 'Validation Specialists', 'Client Managers', 'Data Ops'],
      flow: [
        { label: 'Acquire', sub: 'At scale', icon: 'Bot' },
        { label: 'Parse', sub: 'Every commodity', icon: 'ScanLine' },
        { label: 'Structure', sub: 'Consistent data', icon: 'Database' },
        { label: 'Flag', sub: 'Exceptions', icon: 'AlertTriangle' },
        { label: 'Report', sub: 'Client-ready', icon: 'BarChart3' },
      ],
      result: 'Review more bills. Find more savings.',
      chips: [
        { label: 'Every commodity', icon: 'Layers' },
        { label: 'Exceptions surfaced', icon: 'AlertTriangle' },
        { label: 'API & feeds', icon: 'Plug' },
        { label: 'Client-ready', icon: 'FileCheck' },
      ],
    },
    problems: [
      'Manual acquisition and extraction limit how many bills you can review',
      'Inconsistent data quality undermines confident validation',
      'Hard to scale across more clients and accounts',
      'Fragmented tooling for data, exceptions and client reporting',
    ],
    capabilities: [
      { icon: 'Bot', title: 'Automated acquisition', text: 'Collection Agents and multi-channel capture feed bills in at scale.' },
      { icon: 'Sparkles', title: 'Accurate parsing', text: 'High-accuracy data capture across all commodities, including meter reads.' },
      { icon: 'BadgeCheck', title: 'Validation & exceptions', text: 'Integrity, tolerance and comparison checks surface anomalies to review.' },
      { icon: 'Layers', title: 'Structured data & API', text: 'Clean, structured data and exports/API to plug into your own workflows.' },
      { icon: 'Sparkles', title: 'White-label client portal', text: 'A branded portal so your clients see the experience under your name.' },
      { icon: 'ShieldCheck', title: 'Audit trails', text: 'Page-level traceability and complete history for defensible findings.' },
    ],
    outcomes: [
      'Review more bills with the same team',
      'Consistent, defensible, validated data',
      'Scale across clients and accounts with ease',
      'A branded experience that elevates your service',
    ],
    faq: [
      { q: 'We run a utility bill validation / audit practice — can BillPortal support us?', a: 'Yes. We provide the data backbone — automated acquisition, accurate parsing and structured, validated data across commodities — so your team can focus on identifying issues and savings while you stay in control of the client relationship and the data. We support validation and audit providers with both the platform and, where you want it, managed resources behind the scenes.' },
      { q: 'Can we white-label it for our clients?', a: 'Yes — you offer a branded portal and reports so your clients experience the service under your name, while you own the underlying data and workflow.' },
      { q: 'Can we plug the data into our own tools?', a: 'Yes — structured data, exports and APIs let you integrate BillPortal data into your existing validation and reporting workflows, so it fits the way your team already works.' },
    ],
    caseStudy: 'global-commercial-real-estate',
  },
};

export const SOLUTION_LIST = Object.values(SOLUTIONS);

/* ------------------------------------------------------------------ */
/* Case studies (anonymised — no client names)                         */
/* ------------------------------------------------------------------ */
export const CASE_STUDIES = [
  {
    slug: 'multifamily-portfolio-uem',
    segment: 'Multifamily',
    image: IMAGES.multifamily,
    title: 'Transforming utility expense management for a multifamily portfolio',
    excerpt:
      'A portfolio of 12 multifamily operators across 14 U.S. states centralised utility operations and shifted from manual processing to AI-driven, exception-based control.',
    profile: '12 multifamily operators · 14 U.S. states',
    metrics: [
      { value: '$30.56M', label: 'Utility spend under management' },
      { value: '22,741', label: 'Utility bills managed' },
      { value: '10,588', label: 'Payment transactions processed' },
      { value: '$8.9M', label: 'Value of payment transactions' },
      { value: '232', label: 'Payment files ($15.74M approved)' },
      { value: '3,335', label: 'Invoices flagged for late-fee exposure' },
    ],
    challenge: [
      'Managing utilities across a growing multifamily portfolio was hindered by manual, inefficient processes.',
      'Teams had to coordinate thousands of invoices across many suppliers, manage complex approvals, ACH file generation and reconciliations, recover vacant-unit costs, and operate with limited visibility into missing invoices — increasing the risk of late payments and the operational effort required by staff.',
    ],
    solution: [
      'An AI-driven Utility Expense Management platform automated the entire utility expense lifecycle.',
      'AI-powered bill ingestion and intelligent data extraction — including charges, usage and meter reads across every commodity — fed automated validation and approval workflows. ACH/NACHA payment orchestration, vendor-portal automation and vendor follow-up handled execution, while continuous missing-, late- and overdue-bill tracking kept data complete.',
      'Properties were organised by common area and unit, with end-to-end audit trails giving finance and property teams a single source of truth.',
    ],
    approach: [
      'Rather than simply digitising invoices, the project transformed the end-to-end utility expense lifecycle.',
      'AI Agents monitor, validate and process utility data and payments — allowing finance and property teams to shift from manual processing to exception-based management and oversight, with humans in the loop.',
    ],
    results: [
      'Centralisation of utility operations into a single platform',
      'Reduction of manual processing overhead through AI and automation',
      'Increased visibility into invoice status and approval stages',
      'Faster payment processing and reconciliation cycles',
      'Meter reads captured for compliance, rebilling and ratio billing',
      'Proactive risk management on missing, late and overdue invoices',
    ],
    quote: {
      text: 'Rather than simply digitising invoices, the platform transformed the end-to-end utility expense lifecycle — enabling finance teams to operate with greater speed, visibility, accuracy and control while supporting continued portfolio growth.',
      attribution: 'Programme summary',
    },
  },
  {
    slug: 'global-commercial-real-estate',
    segment: 'Commercial & Industrial',
    image: IMAGES.commercial,
    title: 'Automating utility bill management for a global real estate services firm',
    excerpt:
      'A global commercial real estate services firm replaced a 50-person manual operation and a fragmented vendor stack with an integrated, automated utility bill management platform.',
    profile: '10,000+ utility accounts · $500M+ annual spend · 6 countries',
    metrics: [
      { value: '$750K+', label: 'Annual savings' },
      { value: '≥60%', label: 'Lower payment-processing cost' },
      { value: '100%', label: 'Key reports automated' },
      { value: '0', label: 'Late-payment penalties' },
      { value: '10 yrs', label: 'Historical data migrated' },
      { value: '99%', label: 'Platform uptime target' },
    ],
    challenge: [
      'A global real estate services firm managed more than 10,000 utility accounts with over $500M of annual spend across six countries.',
      'Utility bill management was complex and resource-heavy: a large in-house operations team and vendor-managed technology drove high overheads and error-prone manual processes, while a lack of centralised data led to double-handling across AP, finance, procurement and sustainability teams.',
    ],
    solution: [
      'An automated, fully integrated utility bill management system was implemented.',
      'A white-labelled client portal provided dashboards, reports, contracts and accrual summaries. Automated invoice processing handled acquisition, data extraction, validation and issue resolution using market and grid data.',
      'A two-way integration with the ERP enabled straight-through processing for Accounts Payable — including payment approvals and remittances — and a sustainability integration enabled emissions calculations and reporting.',
    ],
    approach: [
      'The project paired onshore regional leadership with an offshore implementation partner, using an agile methodology.',
      'It audited ten years of historical data for migration, developed SOPs for business-as-usual support, and implemented a phased handover and training strategy with strict SLAs — 24–48 hour turnarounds for collection, parsing and issue resolution, plus a 99% platform uptime target.',
    ],
    results: [
      'Over $750,000 per annum in savings',
      'Internal payment-processing costs reduced by at least 60%',
      'Total elimination of late-payment penalties',
      'Significant reduction in the headcount required to manage AP',
      '100% automation of key reports',
      'Centralised portfolio visibility for data-driven decisions',
    ],
    quote: {
      text: 'A managed, integrated platform replaced fragmented manual processes — delivering measurable savings, eliminating late penalties and giving every team a single, trusted view of utility data.',
      attribution: 'Programme highlights',
    },
  },
];

export const getCaseStudy = (slug) => CASE_STUDIES.find((c) => c.slug === slug);

/* ------------------------------------------------------------------ */
/* Blog / resources                                                    */
/* ------------------------------------------------------------------ */
export const BLOG_POSTS = [
  {
    slug: 'hidden-costs-manual-utility-bill-management',
    category: 'Operations',
    title: 'The 5 hidden costs of manual utility bill management',
    excerpt: 'Late fees are only the beginning. Here are the costs that quietly erode finance teams managing utilities by hand.',
    author: 'BillPortal Team',
    date: '2025-06-18',
    readTime: 6,
    image: IMAGES.heroAnalytics,
    body: [
      { heading: 'Manual is more expensive than it looks', paragraphs: ['Utility bills are high-volume and arrive from dozens of suppliers across portals, email and post. When the process is manual, the visible costs — staff hours and the occasional late fee — hide much larger structural costs that compound month after month.'] },
      { heading: '1. Late fees and disconnection risk', paragraphs: ['Missed due dates trigger penalties and, in the worst cases, disconnection. With thousands of bills, even a small miss rate adds up to material spend and operational disruption.'] },
      { heading: '2. Missing bills you never see', paragraphs: ['You can only pay what you receive. Without active tracking of missing and late-arriving bills, gaps go unnoticed until they surface as back-bills, accruals or compliance problems.'] },
      { heading: '3. Double-handling across teams', paragraphs: ['AP, finance, procurement and sustainability each re-key the same data into different systems. Every hand-off introduces delay and the opportunity for error.'] },
      { heading: '4. Decisions made on stale data', paragraphs: ['Budgets, accruals and reporting depend on complete, validated data. Manual processes deliver it late — if at all — so decisions lag reality.'] },
      { heading: '5. Audit and compliance exposure', paragraphs: ['Without end-to-end audit trails, proving what was paid, when and why becomes a project in itself. Automation makes the audit a by-product, not a fire drill.'] },
      { heading: 'The shift to exception-based control', paragraphs: ['Automating collection, parsing, validation and payments lets teams manage by exception — reviewing only what needs attention, with AI Agents doing the work and humans staying in the loop.'] },
    ],
  },
  {
    slug: 'vacant-cost-recovery-multifamily-guide',
    category: 'Multifamily',
    title: 'Vacant Cost Recovery in multifamily: a practical guide',
    excerpt: 'VCR can recover meaningful utility spend on vacant units — but only with occupancy-aware, validated data.',
    author: 'BillPortal Team',
    date: '2025-06-02',
    readTime: 7,
    image: IMAGES.multifamily,
    body: [
      { heading: 'What is Vacant Cost Recovery?', paragraphs: ['Vacant Cost Recovery (VCR) is the practice of identifying utility costs incurred on vacant units and recovering them rather than absorbing them as operating expense. Done well, it turns a leak into a recoverable line item and protects NOI.'] },
      { heading: 'Why it is hard to do manually', paragraphs: ['VCR depends on knowing which units were vacant, when, and which charges relate to them. That means joining occupancy data with unit-level, validated bill data — exactly the kind of reconciliation that breaks down in spreadsheets.'] },
      { heading: 'The data foundation', paragraphs: ['Start with a clean hierarchy of property, common area and unit, with accounts clearly separated. Then layer in occupancy status so charges can be attributed correctly, and capture bills that arrive by post for vacant units.'] },
      { heading: 'Operationalising VCR', paragraphs: ['With validated bills and occupancy in one platform, VCR becomes a repeatable workflow: detect, attribute, review the exceptions, and report. The result is recovered spend and a defensible audit trail.'] },
    ],
  },
  {
    slug: 'data-foundation-energy-brokers',
    category: 'Energy Brokers',
    title: 'From bills to budgets: building a data foundation for energy brokers',
    excerpt: 'Contracting and budgeting are only as good as the data beneath them. Here is how brokers build that foundation.',
    author: 'BillPortal Team',
    date: '2025-05-20',
    readTime: 6,
    image: IMAGES.grid,
    body: [
      { heading: 'Procurement is not enough', paragraphs: ['Winning a contract is the start, not the finish. Clients increasingly expect brokers to help them manage spend continuously — which requires reliable, structured data across their sites.'] },
      { heading: 'Collection and parsing first', paragraphs: ['Automated collection and AI parsing of bills create the foundation. Once consumption and cost are validated and structured, contracting and budgeting modules have something solid to stand on.'] },
      { heading: 'Contracting against real consumption', paragraphs: ['With validated data, you can model contracts and rates against actual usage — and demonstrate value with evidence rather than assertions.'] },
      { heading: 'Budgeting and reforecasting', paragraphs: ['Lock budgets, set targets and reforecast as conditions change. Alerts on anomalies let you intervene before a client overruns.'] },
    ],
  },
  {
    slug: 'ach-nacha-ctx-ccd-utility-payments',
    category: 'Payments',
    title: 'ACH, NACHA CTX & CCD+: choosing the right utility payment rail',
    excerpt: 'A plain-English look at the ACH formats behind reliable, reconcilable bulk utility payments.',
    author: 'BillPortal Team',
    date: '2025-05-06',
    readTime: 5,
    image: IMAGES.heroAnalytics,
    body: [
      { heading: 'Why the format matters', paragraphs: ['Paying a utility is easy; reconciling thousands of payments is not. The ACH addenda you use determine how much remittance detail travels with each payment — and therefore how easily it reconciles.'] },
      { heading: 'CCD+ for streamlined remittance', paragraphs: ['CCD+ carries a single addenda record — enough to reference an invoice or account, ideal for high-volume, straightforward bulk payments.'] },
      { heading: 'CTX for rich, structured detail', paragraphs: ['CTX supports many addenda records, allowing structured remittance for complex, multi-invoice payments — useful when a single payment covers many bills.'] },
      { heading: 'Letting the platform decide', paragraphs: ['The right choice depends on the supplier and the payment. BillPortal generates compliant files automatically and attaches remittance, so reconciliation is a by-product, not a chore.'] },
    ],
  },
  {
    slug: 'energy-star-portfolio-manager-compliance-data',
    category: 'Sustainability',
    title: 'ENERGY STAR® Portfolio Manager: turning bills into compliance-ready data',
    excerpt: 'Benchmarking and compliance start with complete, validated consumption data. Here is how to get there.',
    author: 'BillPortal Team',
    date: '2025-04-22',
    readTime: 6,
    image: IMAGES.solar,
    body: [
      { heading: 'Compliance follows data quality', paragraphs: ['Benchmarking a building or meeting a reporting mandate depends on complete, accurate consumption data. Gaps and errors in bills become gaps and errors in your scores.'] },
      { heading: 'From bills to consumption', paragraphs: ['Validated bills already contain the usage and meter-read data you need. The trick is structuring it cleanly by property and meter, with missing periods detected and resolved.'] },
      { heading: 'Feeding Portfolio Manager', paragraphs: ['With validated data in one place, feeding ENERGY STAR® Portfolio Manager becomes a controlled, repeatable process rather than a manual scramble each reporting cycle.'] },
      { heading: 'Beyond compliance', paragraphs: ['The same data supports emissions accounting across Scope 1, 2 and 3 — so sustainability reporting and financial control draw on one trusted source.'] },
    ],
  },
  {
    slug: 'ai-bill-parsing-accuracy-meter-reads',
    category: 'Product',
    title: 'Accurate bill parsing: from charges to meter reads',
    excerpt: 'Extracting text is easy. Capturing every charge and meter read accurately — and validating it — is the real unlock.',
    author: 'BillPortal Team',
    date: '2025-04-08',
    readTime: 5,
    image: IMAGES.heroAnalytics,
    body: [
      { heading: 'Reading is not understanding', paragraphs: ['A string of numbers is not the same as a validated charge tied to the right account and meter. Accurate parsing places every value in a consistent structure across every commodity.'] },
      { heading: 'Every commodity, every charge', paragraphs: ['Electricity, water, waste, gas, heating oil, telco and fuels each present data differently. AI Agents capture line-items, charges, usage and meter reads accurately across all of them.'] },
      { heading: 'Why meter reads matter', paragraphs: ['Meter reads from bills power compliance reporting, rebilling and ratio billing — so capturing them accurately is essential, not optional.'] },
      { heading: 'Validation builds trust', paragraphs: ['Arithmetic, tolerance and comparison checks confirm the numbers make sense, and page-level references tie each value back to its source — auditable for payments and compliance alike.'] },
    ],
  },
];

export const getBlogPost = (slug) => BLOG_POSTS.find((p) => p.slug === slug);

/* ------------------------------------------------------------------ */
/* Implementation journey (from a representative onboarding plan)      */
/* ------------------------------------------------------------------ */
export const IMPLEMENTATION_PHASES = [
  { key: 'initiation', icon: 'FileSignature', title: 'Initiation', summary: 'Contract, compliance and mobilisation', text: 'We sign the contract, complete compliance steps, review and sign the Letter of Authorisation (LOA), and mobilise the full project team and plan.' },
  { key: 'discovery', icon: 'Search', title: 'Discovery', summary: 'Data, portals and integration requirements', text: 'We discover and prepare onboarding data for sites, accounts and suppliers, gather portal credentials, and document integration and data-transfer requirements.' },
  { key: 'execution', icon: 'Workflow', title: 'Execution', summary: 'Configure, integrate and migrate', text: 'We onboard sites and accounts, configure Collection Agents and validation, build integrations, run UAT and migrate historical data with reconciliation.' },
  { key: 'cutover', icon: 'Rocket', title: 'Cut-over & Hyper Care', summary: 'Go-live with dedicated monitoring', text: 'We run parallel processing, cut over to live, and a dedicated team monitors SLAs across all streams during hyper care to resolve any issue immediately.' },
];

export const IMPLEMENTATION_STREAMS = [
  { icon: 'FileSignature', title: 'Contract & compliance', text: 'Contract signing, compliance and SOPs/OKRs defined for business-as-usual operations.' },
  { icon: 'Database', title: 'Data collection', text: 'Onboarding data sets, portal credentials, supplier mail routes and historical-bill analysis.' },
  { icon: 'Workflow', title: 'Onboarding (BAU)', text: 'Sites and accounts, Collection Agents, validation, platform configuration and operations team mobilisation.' },
  { icon: 'Network', title: 'Platform integrations', text: 'Upstream/downstream integrations for payments and bill data, plus two-way sustainability sync.' },
  { icon: 'DatabaseZap', title: 'Historical data', text: 'Automated, secure migration of historical data with trench-by-trench reconciliation and sign-off.' },
  { icon: 'LifeBuoy', title: 'Hyper care', text: 'Dedicated teams monitor SLAs across all UBM streams and respond immediately to any disruption.' },
];

/* ------------------------------------------------------------------ */
/* Testimonials (anonymised, role-based)                               */
/* ------------------------------------------------------------------ */
export const TESTIMONIALS = [
  { quote: 'We moved from chasing bills to managing exceptions. Our team finally has time to do finance, not data entry.', role: 'VP of Finance', org: 'Multifamily operator' },
  { quote: 'Centralising utility data across countries gave every team — AP, procurement and sustainability — one version of the truth.', role: 'Director of Operations', org: 'Global real estate services firm' },
  { quote: 'The data foundation lets us show clients real, evidence-based savings. It changed how we sell.', role: 'Managing Partner', org: 'Energy brokerage' },
];

/* ------------------------------------------------------------------ */
/* FAQ (general — used on Home / Platform)                             */
/* ------------------------------------------------------------------ */
export const FAQ = [
  { q: 'What does BillPortal actually do?', a: 'BillPortal gives you ownership of the end-to-end utility bill lifecycle — collection, validation, payments and reporting — in one auditable workspace you control. It automates bill acquisition from portals, email, upload, SFTP and post; captures and validates the data with AI Agents; proactively tracks missing, late and overdue bills; and pays your vendors in bulk. The difference: you own the software and your data. Your bills, vendors, GL mappings, payment-file formats and integrations stay with you — so instead of re-migrating to a new incumbent every couple of years, you keep full transparency and control and simply choose who operates it.' },
  { q: 'How do you handle missing and late-arriving bills?', a: 'Missing bills are the biggest — and least talked-about — problem in the industry. Operations teams spend their days chasing them, and every gap quietly turns into late fees, disconnections and broken reporting. BillPortal makes this proactive and agent-powered. The moment a bill skips its expected issue date, we flag it as a late-arriving bill so your team sees the gap immediately — no waiting for something to go wrong. From the late-arriving / missing bills view, Operations can trigger a Collection Agent to fetch the bill directly, or ask their AI Support Agent to reach out to vendors in bulk. The agent builds a missing-bills report per vendor, drafts the chasing emails and lets you review and approve — once approved, the emails go out to vendors with you in CC. Work that used to take days is done in minutes, with no more hand-writing and tracking dozens of emails. Teams log a note and assign the task to themselves, so there is no overlap or duplication — total control. And because every chase and vendor communication sits in a full audit trail, if a bill or charge is ever disputed you can challenge the vendor with the evidence in hand.' },
  { q: 'Is it self-serve or managed?', a: 'Both — and the choice stays yours. Self-serve means you own and run the platform in-house with your own team, with AI Agents automating and orchestrating the work behind the scenes. Managed service is best thought of as an extended team, not outsourcing: we provide dedicated offshore resourcing and infrastructure, fully trained on the platform, to support your business operations and work to your KPIs. You direct them like your own team — your team, your data and your process stay entirely under your control. Everything is fully transparent: you see the people, the work they do and the cost, so there is no black box. Because that resourcing and the software pricing are open to you, it is far more economical than a traditional fully-managed contract, and with our combined model we are confident you will see real savings and improved efficiency. In short, self-serve or managed, you always own your data and stay in control — we simply flex how much of the day-to-day operating we resource for you.' },
  { q: 'Do you use third-party services for bill collection or data extraction?', a: 'No — it is all our own proprietary technology. We built our own portal Collection Agents to acquire bills from utility portals, and our own extraction and validation algorithms, embedded with AI to carry data context across the platform. Our engine handles complex bill formats — single bills, collective and consolidated bills — and our in-house OCR reads scanned copies too. Our portal agents also handle 2FA and verification, so we can cover the full collection space. If you use a portal that is not yet on our list, that is not a problem: we typically build support in 3–4 days at most (sometimes within 24 hours), and we test it thoroughly before going live so we set the right expectations. In short, we have full control over our own tech stack — which is exactly how we pass that control back to you.' },
  { q: 'How do you collect bills?', a: 'Through Collection Agents on supplier portals (covering most U.S. utilities), a dedicated email inbox, manual upload, SFTP feeds and scanned postal mail — all flowing into one secure, centralised repository, alongside interval and meter data.' },
  { q: 'How do payments work?', a: 'We consolidate verified bills into payment files with GL and cost-code allocation, then pay your vendors in bulk by ACH (NACHA CTX and CCD+). Credit card and cheque are available through our managed service and banking partner, with remittance attached to every payment.' },
  { q: 'Which systems do you integrate with?', a: 'ERP and AP systems such as Yardi, SAP, Xero and JD Edwards; sustainability platforms like Envizi and Watershed; ENERGY STAR® Portfolio Manager; and energy systems (EMS/DER) for solar, batteries and EV — with two-way sync where supported.' },
  { q: 'Where is my data stored?', a: 'We store data on servers within your country to support local data-residency requirements and give you peace of mind on security.' },
];

/* ------------------------------------------------------------------ */
/* Company values / about                                              */
/* ------------------------------------------------------------------ */
export const ABOUT_VALUES = [
  { icon: 'Sparkles', title: 'Automation-first', text: 'AI Agents automate the repetitive so people can focus on judgement and exceptions.' },
  { icon: 'ShieldCheck', title: 'Trust & accuracy', text: 'Validated data and complete audit trails underpin everything we ship.' },
  { icon: 'Leaf', title: 'Sustainability', text: 'We help organisations measure and reduce their environmental impact.' },
  { icon: 'Users', title: 'Partnership', text: 'We align our roadmap to organisations and grow with their portfolios.' },
];

export const ABOUT_STATS = [
  { value: '40+ yrs', label: 'Collective UBM experience' },
  { value: 'APAC · USA · UK · EU', label: 'Markets delivered across' },
  { value: '100%', label: 'Your data, your ownership' },
  { value: '360°', label: 'Transparency, audit-ready' },
];

/* Migration pain — what switching an incumbent really costs */
export const ABOUT_MIGRATION_PAINS = [
  { icon: 'FileText', title: 'Historical bills', text: 'Years of invoices and interval data re-migrated into yet another system.' },
  { icon: 'Landmark', title: 'Vendors & bank accounts', text: 'Every vendor and payment bank account re-established from scratch.' },
  { icon: 'FileSpreadsheet', title: 'GL codes & payment files', text: 'Cost codes, GL mappings and payment-file formats rebuilt to spec.' },
  { icon: 'Plug', title: 'Financial integrations', text: 'Re-wiring Yardi, RealPage, Entrata, SAP, JD Edwards and other ERPs.' },
  { icon: 'Leaf', title: 'Sustainability reporting', text: 'Reconnecting ENERGY STAR Portfolio Manager, Envizi and reporting portals.' },
  { icon: 'Search', title: 'Procurement data', text: 'Chasing and reshaping bill data for procurement teams all over again.' },
];

/* How BillPortal gives control back */
export const ABOUT_CONTROL = [
  { icon: 'Lock', title: 'Own your software', text: 'Full ownership of the platform with every capability to collaborate, work and audit.' },
  { icon: 'Database', title: 'Own your data', text: 'Your bills, vendors, mappings and history stay with you — always.' },
  { icon: 'Eye', title: '360° transparency', text: 'One audit-ready workspace with a complete trail across the entire bill lifecycle.' },
  { icon: 'RefreshCw', title: 'Change on your terms', text: 'Switch your incumbent or bring it in-house — without another painful migration.' },
  { icon: 'Users', title: 'Purpose-built for users', text: 'Designed with finance, AP, operations, procurement, contract and sustainability teams.' },
  { icon: 'ShieldCheck', title: 'No more rip-and-replace', text: 'End the two-year migration cycle and the disruption when key people move on.' },
];

/* ------------------------------------------------------------------ */
/* Leadership / Executive team                                         */
/* ------------------------------------------------------------------ */
export const ABOUT_TEAM = [
  {
    name: 'Domenic Chiavone',
    role: 'Co-founder and Non-Executive Director',
    initials: 'DC',
    location: 'Melbourne, Australia',
    linkedin: 'https://www.linkedin.com/in/domenic-chiavone-6b827247/',
    photo: '/domenic-chiavone.png',
    bio: 'Domenic brings over two decades of leadership across energy, technology and commercial strategy, with deep expertise in strategic utility spend, energy procurement and emissions management. He holds a Master\u2019s in Corporate Environment & Sustainability Management from Monash University alongside a background in international business and marketing. As a Co-founder and Non-Executive Director of BillPortal, he helps steer the company\u2019s vision and the roadmap that unifies bill acquisition, AI-driven data extraction, payments and sustainability reporting \u2014 pairing intelligent automation with human judgement so teams can manage by exception rather than drown in paperwork. He is driven by using technology to give organisations a genuine competitive advantage in how they manage utility cost and carbon. Outside of work, he is an active voice in the energy-transition community and enjoys the outdoors and mentoring early-stage founders.',
  },
  {
    name: 'Hari Prasad',
    role: 'Founder & Director of Operations',
    initials: 'HP',
    location: 'Bengaluru, India',
    linkedin: 'https://www.linkedin.com/in/mail2hari/',
    photo: '/hari-prasad.png',
    bio: 'Hari is a technologist and operator with deep expertise in data analytics, AI and building the systems that power utility bill management at scale \u2014 he founded Proziod Analytics before shaping BillPortal Technologies, and holds an Executive MBA in Finance. As Founder and Director of Operations, he owns the delivery engine: the parsing pipelines, validation checks, SLAs and expert teams that keep bills collected, accurate and paid on time across the entire bill lifecycle. He obsesses over data accuracy and operational efficiency, believing a lean, well-instrumented process is what lets organisations trust the numbers. Away from the platform, he is a lifelong learner drawn to emerging AI research and enjoys tinkering with new tools and technologies.',
  },
  {
    name: 'Thomas Beausang',
    role: 'VP, Americas — Strategic Growth & Partnerships',
    initials: 'TB',
    location: 'Boston, USA',
    linkedin: 'https://www.linkedin.com/in/thomasjbeausang-40a605102/',
    photo: '/thomas-beausang.webp',
    bio: 'Thomas is a commercial energy and utility executive and a self-described expert at building partnerships, with a career spent initiating go-to-market strategies and growing new business across the U.S. energy and utility markets. His background spans energy advisory, utility bill management, deregulated energy markets and building-compliance reporting \u2014 consistently taking brands into new territories and scaling them from the ground up. As VP for the Americas, he leads growth and partnerships for BillPortal, expanding the partner ecosystem, opening new markets and ensuring organisations realise measurable value from day one. He thrives on the consultative side of the business, translating on-the-ground challenges into partnerships that scale. Outside of work, Thomas is a keen traveller and sports enthusiast who values time with family and community.',
  },
];

/* ------------------------------------------------------------------ */
/* Form select options                                                 */
/* ------------------------------------------------------------------ */
export const SEGMENT_OPTIONS = [
  { value: 'multifamily', label: 'Multifamily' },
  { value: 'commercial_industrial', label: 'Commercial & Industrial' },
  { value: 'energy_brokers', label: 'Energy Broker' },
  { value: 'bill_validation', label: 'Bill Validation Provider' },
  { value: 'other', label: 'Other' },
];

export const INTEREST_OPTIONS = [
  { value: 'full_ubm', label: 'Full Utility Bill Management' },
  { value: 'bill_acquisition', label: 'Bill Acquisition' },
  { value: 'bill_parsing', label: 'Bill Parsing & Validation' },
  { value: 'payments', label: 'Payments' },
  { value: 'sustainability', label: 'Sustainability & Reporting' },
  { value: 'partnership', label: 'Partnership / White-label' },
  { value: 'other', label: 'Other' },
];

export const PORTFOLIO_OPTIONS = [
  { value: '1-5000', label: '1 – 5,000 bills / month' },
  { value: '5000-15000', label: '5,000 – 15,000 bills / month' },
  { value: '15000+', label: '15,000+ bills / month' },
];

// ---- Partner programme ----
export const PARTNER_INTEREST_OPTIONS = [
  { value: 'bill_collection', label: 'Bill Collection' },
  { value: 'data_extraction', label: 'Data Extraction' },
  { value: 'ubm_payments', label: 'UBM Payments' },
  { value: 'ubm_services', label: 'UBM Services' },
  { value: 'ubm_migration', label: 'UBM Migration' },
  { value: 'platform_as_a_service', label: 'Platform as a Service' },
  { value: 'other', label: 'Other' },
];

export const PARTNER_ROLE_OPTIONS = [
  { value: 'product_manager', label: 'Product Manager' },
  { value: 'operations_manager', label: 'Operations Manager' },
  { value: 'business_owner', label: 'Business Owner' },
  { value: 'energy_broker', label: 'Energy Broker' },
  { value: 'account_manager', label: 'Account Manager' },
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'other', label: 'Other' },
];

export const EMPLOYEE_COUNT_OPTIONS = [
  { value: '1-10', label: '1 – 10' },
  { value: '11-50', label: '11 – 50' },
  { value: '51-200', label: '51 – 200' },
  { value: '201-500', label: '201 – 500' },
  { value: '501-1000', label: '501 – 1,000' },
  { value: '1000+', label: '1,000+' },
];

export const PARTNER_TYPES = [
  { icon: 'Inbox', title: 'Bill Collection', desc: 'Automated multi-channel bill acquisition — supplier portals, email, upload, SFTP and postal mail — delivered under your brand.' },
  { icon: 'ScanLine', title: 'Data Extraction', desc: 'AI-powered parsing and validation that turns any utility bill into clean, structured, audit-ready data via API.' },
  { icon: 'CreditCard', title: 'UBM Payments', desc: 'Bulk ACH files, card and cheque disbursements with human-in-the-loop approval for your clients.' },
  { icon: 'Handshake', title: 'UBM Services', desc: 'A fully managed utility bill management back office, run on your behalf as a white-label service.' },
  { icon: 'Workflow', title: 'UBM Migration', desc: 'Move clients off legacy tools with structured historical data migration and guided onboarding.' },
  { icon: 'Layers', title: 'Platform as a Service', desc: 'Embed the full BillPortal engine into your own product through our APIs and integrations.' },
];

export const PARTNER_BENEFITS = [
  { icon: 'Rocket', title: 'Go to market faster', desc: 'Launch utility bill management for your clients without building the parsing, payments and data pipeline yourself.' },
  { icon: 'Palette', title: 'White-label ready', desc: 'Your brand, your portal. We stay in the background and power the experience end-to-end.' },
  { icon: 'ShieldCheck', title: 'Enterprise-grade', desc: 'Audit trails, human-in-the-loop review, data residency options and SLA-backed processing.' },
  { icon: 'TrendingUp', title: 'Grow recurring revenue', desc: 'Add a high-retention, credit-based service line with clear per-account economics.' },
];
